import type { ReactNode } from 'react'
import type { ShellData } from '@/lib/market/types'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { Menu, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { primaryNav } from '@/constants/site'
import IndexStrip from '@/components/market/index-strip'
import MarketStatus from '@/components/market/market-status'
import SearchDialog from '@/components/market/search-dialog'

interface SiteHeaderProps {
    shell: ShellData | null
}

const isActive = (pathname: string, href: string): boolean => {
    if (href === '/') return pathname === '/'
    if (href === '/equities') return pathname === '/equities' || pathname.startsWith('/stocks')

    return pathname === href || pathname.startsWith(`${href}/`)
}

export const StatusBar = ({ shell }: SiteHeaderProps): ReactNode => {
    return (
        <div className='border-b border-border bg-card/40'>
            <div className='mx-auto flex h-9 max-w-[1440px] items-center justify-between gap-4 px-4 sm:px-6'>
                <div className='flex min-w-0 items-center gap-3'>
                    <MarketStatus session={shell?.session ?? null} />

                    <a
                        href='#data-notice'
                        className='hidden shrink-0 rounded border border-border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground transition-colors hover:text-foreground lg:inline'
                    >
                        Simulated data
                    </a>
                </div>

                <IndexStrip items={shell?.indices ?? []} className='hidden md:flex' />
            </div>
        </div>
    )
}

export const SiteHeader = ({ shell }: SiteHeaderProps): ReactNode => {
    const router = useRouter()
    const [menuOpen, setMenuOpen] = useState(false)

    useEffect(() => {
        const close = () => setMenuOpen(false)
        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    return (
        <header className='sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur-md supports-[backdrop-filter]:bg-background/70'>
            <div className='mx-auto flex h-14 max-w-[1440px] items-center gap-6 px-4 sm:px-6'>
                <Logo />

                <nav aria-label='Primary' className='hidden min-w-0 flex-1 items-center gap-0.5 lg:flex'>
                    {primaryNav.map(item => {
                        const active = isActive(router.pathname, item.href)

                        return (
                            <Link
                                key={item.href}
                                href={item.href}
                                aria-current={active ? 'page' : undefined}
                                className={cn(
                                    'relative rounded-md px-2.5 py-1.5 text-[13px] font-medium transition-colors xl:px-3',
                                    active ? 'text-foreground' : 'text-muted-foreground hover:bg-secondary/70 hover:text-foreground'
                                )}
                            >
                                {item.label}
                                {active && <span aria-hidden='true' className='absolute inset-x-2.5 -bottom-[11px] h-0.5 rounded-full bg-primary xl:inset-x-3' />}
                            </Link>
                        )
                    })}
                </nav>

                <div className='ml-auto flex items-center gap-2 lg:ml-0'>
                    <SearchDialog items={shell?.search ?? []} />

                    <button
                        type='button'
                        onClick={() => setMenuOpen(value => !value)}
                        aria-expanded={menuOpen}
                        aria-controls='mobile-navigation'
                        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                        className='inline-flex size-9 items-center justify-center rounded-lg border border-border bg-secondary/70 text-foreground lg:hidden'
                    >
                        {menuOpen ? <X aria-hidden='true' className='size-4' /> : <Menu aria-hidden='true' className='size-4' />}
                    </button>
                </div>
            </div>

            {menuOpen && (
                <nav id='mobile-navigation' aria-label='Mobile' className='border-t border-border bg-background lg:hidden'>
                    <ul className='mx-auto grid max-w-[1440px] gap-1 px-4 py-3 sm:grid-cols-2 sm:px-6'>
                        {primaryNav.map(item => {
                            const active = isActive(router.pathname, item.href)

                            return (
                                <li key={item.href}>
                                    <Link
                                        href={item.href}
                                        aria-current={active ? 'page' : undefined}
                                        className={cn(
                                            'block rounded-lg px-3 py-2.5 transition-colors',
                                            active ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground'
                                        )}
                                    >
                                        <span className='block text-sm font-medium'>{item.label}</span>
                                        <span className='block text-xs text-muted-foreground'>{item.description}</span>
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>

                    {shell && (
                        <div className='border-t border-border px-4 py-3 md:hidden'>
                            <IndexStrip items={shell.indices} className='flex-wrap' />
                        </div>
                    )}
                </nav>
            )}
        </header>
    )
}
